import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  acceptCustomerInvitation,
  declineCustomerInvitation,
  previewCustomerInvitation,
  type CustomerInvitationPreviewResponse,
} from "@/api/auth";
import { ApiError, apiErrorMessage } from "@/api/client";
import { AuthShell } from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

export function InvitationPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<CustomerInvitationPreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const inspect = async () => {
    setBusy(true); setMessage("");
    try { setPreview(await previewCustomerInvitation(code)); }
    catch (error) { setPreview(null); setMessage(invitationMessage(error)); }
    finally { setBusy(false); }
  };

  const accept = async () => {
    setBusy(true); setMessage("");
    try {
      await acceptCustomerInvitation(code);
      setCode("");
      await refreshMe();
      navigate("/console/dashboard", { replace: true });
    } catch (error) { setMessage(invitationMessage(error)); setBusy(false); }
  };

  const decline = async () => {
    setBusy(true); setMessage("");
    try {
      await declineCustomerInvitation(code);
      setCode(""); setPreview(null); setMessage("招待を辞退しました。組織所属は変更されていません。");
    } catch (error) { setMessage(invitationMessage(error)); }
    finally { setBusy(false); }
  };

  return <AuthShell title="組織への招待" description="招待メールに記載されたコードを入力し、内容を確認してから承諾または辞退してください。コードをURLへ貼り付けたり保存したりしないでください。">
    <div className="space-y-4">
      {message ? <div role="status" className="rounded-md border bg-muted p-3 text-sm">{message}</div> : null}
      <div className="space-y-2"><Label htmlFor="invitation-code">招待コード</Label><Input id="invitation-code" autoComplete="off" value={code} onChange={(event) => { setCode(event.target.value); setPreview(null); }} /><Button disabled={busy || !/^[A-Za-z0-9_-]{43}$/.test(code)} onClick={() => void inspect()}>招待内容を確認</Button></div>
      {preview ? <div className="space-y-3 rounded-md border p-4"><dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm"><dt className="text-muted-foreground">組織</dt><dd>{preview.organizationName}</dd><dt className="text-muted-foreground">招待先</dt><dd>{preview.maskedRecipientEmail}</dd><dt className="text-muted-foreground">権限</dt><dd>{preview.role === "customer_admin" ? "管理者" : "一般ユーザー"}</dd><dt className="text-muted-foreground">期限</dt><dd>{new Date(preview.expiresAt).toLocaleString("ja-JP")}</dd></dl><p className="text-sm">この招待を承諾すると、組織のメンバーに追加されます。</p><div className="flex gap-2"><Button disabled={busy} onClick={() => void accept()}>この組織に参加</Button><Button variant="outline" disabled={busy} onClick={() => void decline()}>辞退する</Button></div></div> : null}
    </div>
  </AuthShell>;
}

function invitationMessage(error: unknown) {
  if (!(error instanceof ApiError)) return apiErrorMessage(error, "招待を処理できませんでした。");
  if (error.code === "invitation_recipient_mismatch") return "この招待は現在ログイン中のアカウント宛てではありません。";
  if (error.code === "invitation_expired") return "招待の有効期限が切れています。";
  if (error.code === "invitation_revoked") return "招待は取り消されています。";
  if (error.code === "membership_disabled") return "この組織の所属は無効化されています。管理者へ連絡してください。";
  return apiErrorMessage(error, "招待を処理できませんでした。");
}
